import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * תפריט נגישות (Accessibility menu) — נדרש בפועל לפי תקנות שוויון זכויות
 * לאנשים עם מוגבלות (התאמות נגישות לשירות) / ת"י 5568 ≈ WCAG 2.0 AA.
 *
 * רכיב נגישות נטיב (ללא תלות בספק חיצוני/רישיון). מוסיף/מסיר מחלקות על
 * <html> ושומר את ההעדפות ב-localStorage כך שיישמרו בין ביקורים.
 * הרכיב עצמו נגיש: כפתור עם aria-label, פאנל עם role="dialog", ניווט מקלדת,
 * סגירה ב-Esc, והחזרת פוקוס לכפתור הפותח.
 */

type Prefs = {
  fontScale: number; // 0..4 steps
  contrast: 'none' | 'high' | 'dark';
  grayscale: boolean;
  highlightLinks: boolean;
  readableFont: boolean;
  bigCursor: boolean;
  stopAnimations: boolean;
};

const DEFAULT_PREFS: Prefs = {
  fontScale: 0,
  contrast: 'none',
  grayscale: false,
  highlightLinks: false,
  readableFont: false,
  bigCursor: false,
  stopAnimations: false,
};

const STORAGE_KEY = 'a11y-prefs-v1';

// Public-facing routes (no auth) where the accessibility menu must appear —
// the pages external visitors/donors open from shared links, plus the public
// info/auth pages. Mirrors the "Public — no auth" section of App.tsx; the
// authenticated CRM is intentionally excluded. Anchored so an admin path that
// merely contains one of these words can't match.
const PUBLIC_PATHS: RegExp[] = [
  /^\/$/,                                  // marketing home (redirects to /dashboard when logged in)
  /^\/landing$/,
  /^\/campaign\//,                         // public donor page + ambassador/manager dashboards
  /^\/pay\//,                              // standalone payment link
  /^\/payment\//,                          // public payment form
  /^\/payments\/(success|complete|failed)$/,
  /^\/payment-failed$/,
  /^\/thank-you$/,
  /^\/p\//,                                // public tool / landing page
  /^\/form\//,                             // public form
  /^\/quote\//,                            // public price quote
  /^\/strategic-plan\//,
  /^\/volunteers\/public\//,
  /^\/volunteer-portal$/,
  /^\/knowledge(\/|$)/,                     // knowledge base + articles
  /^\/privacy$/,
  /^\/terms$/,
  /^\/accessibility$/,
  /^\/contact$/,
  /^\/register(\/|$)/,
  /^\/quick-register$/,
  /^\/forgot-password$/,
  /^\/reset-password$/,
  /^\/accept-invite$/,
  /^\/u\//,                                // marketing opt-out
];

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_PREFS;
  }
}

function applyPrefs(p: Prefs) {
  const el = document.documentElement;
  // font scale: each step = +12.5%
  el.style.setProperty('--a11y-font-scale', String(1 + p.fontScale * 0.125));
  el.classList.toggle('a11y-font-scaled', p.fontScale > 0);
  el.classList.toggle('a11y-contrast-high', p.contrast === 'high');
  el.classList.toggle('a11y-contrast-dark', p.contrast === 'dark');
  el.classList.toggle('a11y-grayscale', p.grayscale);
  el.classList.toggle('a11y-highlight-links', p.highlightLinks);
  el.classList.toggle('a11y-readable-font', p.readableFont);
  el.classList.toggle('a11y-big-cursor', p.bigCursor);
  el.classList.toggle('a11y-stop-animations', p.stopAnimations);
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState<Prefs>(DEFAULT_PREFS);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  // The accessibility menu is required only on the PUBLIC-facing service —
  // the pages external visitors/donors reach via shared links — not inside the
  // authenticated CRM (where it also collided with editor controls). Show the
  // FAB only on the public routes below; keep this list in sync with the
  // "Public — no auth" routes in App.tsx.
  const onPublicPage = PUBLIC_PATHS.some((re) => re.test(pathname));

  // Load + apply on mount
  useEffect(() => {
    const p = loadPrefs();
    setPrefs(p);
    applyPrefs(p);
  }, []);

  const update = useCallback((patch: Partial<Prefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      applyPrefs(next);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / private mode */
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setPrefs(DEFAULT_PREFS);
    applyPrefs(DEFAULT_PREFS);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  // Esc closes + return focus to trigger
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    // move focus into the panel
    panelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const toggleBtn = (
    active: boolean,
    label: string,
    onClick: () => void
  ) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '10px 12px',
        borderRadius: 8,
        border: active ? '2px solid #1565C0' : '1px solid #cbd5e1',
        background: active ? '#E3F2FD' : '#fff',
        color: '#0f172a',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        textAlign: 'right',
      }}
    >
      <span>{label}</span>
      <span aria-hidden="true">{active ? '✓' : ''}</span>
    </button>
  );

  // Saved preferences still apply to <html> via the effects above; we only
  // suppress the floating button/panel outside the public pages.
  if (!onPublicPage) return null;

  return (
    <>
      {/* Responsive vertical position: on mobile the public pages show a
          full-width sticky "Powered by Amutech" footer bar (~50px tall), so
          the FAB floats just above it. On desktop there's no bar → sit in the
          corner. Media-query lives here because inline styles can't hold one. */}
      <style>{`
        .a11y-fab { bottom: 16px; }
        .a11y-panel { bottom: 80px; }
        @media (max-width: 640px) {
          .a11y-fab { bottom: 62px; }
          .a11y-panel { bottom: 126px; }
        }
      `}</style>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="תפריט נגישות"
        aria-expanded={open}
        aria-haspopup="dialog"
        title="תפריט נגישות"
        className="a11y-fab"
        style={{
          position: 'fixed',
          insetInlineStart: 16,
          zIndex: 2147483646,
          width: 52,
          height: 52,
          borderRadius: '50%',
          border: '2px solid #fff',
          background: '#1565C0',
          color: '#fff',
          fontSize: 26,
          lineHeight: '1',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(0,0,0,.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span aria-hidden="true">♿</span>
      </button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label="הגדרות נגישות"
          tabIndex={-1}
          className="a11y-panel"
          style={{
            position: 'fixed',
            insetInlineStart: 16,
            zIndex: 2147483647,
            width: 320,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 110px)',
            overflowY: 'auto',
            background: '#fff',
            color: '#0f172a',
            border: '1px solid #cbd5e1',
            borderRadius: 14,
            boxShadow: '0 10px 40px rgba(0,0,0,.25)',
            padding: 16,
            direction: 'rtl',
            fontFamily: 'sans-serif',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>נגישות</h2>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                triggerRef.current?.focus();
              }}
              aria-label="סגור תפריט נגישות"
              style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: '#475569', lineHeight: 1 }}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          {/* Font size */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 6 }}>גודל טקסט</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => update({ fontScale: Math.max(0, prefs.fontScale - 1) })}
                aria-label="הקטן טקסט"
                disabled={prefs.fontScale <= 0}
                style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontSize: 18, fontWeight: 700, cursor: prefs.fontScale <= 0 ? 'default' : 'pointer', opacity: prefs.fontScale <= 0 ? 0.5 : 1 }}
              >
                A−
              </button>
              <span style={{ flex: 1, textAlign: 'center', alignSelf: 'center', fontSize: 14, fontWeight: 700 }}>
                {prefs.fontScale === 0 ? 'רגיל' : `+${prefs.fontScale}`}
              </span>
              <button
                type="button"
                onClick={() => update({ fontScale: Math.min(4, prefs.fontScale + 1) })}
                aria-label="הגדל טקסט"
                disabled={prefs.fontScale >= 4}
                style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontSize: 18, fontWeight: 700, cursor: prefs.fontScale >= 4 ? 'default' : 'pointer', opacity: prefs.fontScale >= 4 ? 0.5 : 1 }}
              >
                A+
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            {toggleBtn(prefs.contrast === 'high', 'ניגודיות גבוהה', () =>
              update({ contrast: prefs.contrast === 'high' ? 'none' : 'high' })
            )}
            {toggleBtn(prefs.contrast === 'dark', 'מצב כהה', () =>
              update({ contrast: prefs.contrast === 'dark' ? 'none' : 'dark' })
            )}
            {toggleBtn(prefs.grayscale, 'גווני אפור', () => update({ grayscale: !prefs.grayscale }))}
            {toggleBtn(prefs.highlightLinks, 'הדגשת קישורים', () => update({ highlightLinks: !prefs.highlightLinks }))}
            {toggleBtn(prefs.readableFont, 'גופן קריא', () => update({ readableFont: !prefs.readableFont }))}
            {toggleBtn(prefs.bigCursor, 'סמן גדול', () => update({ bigCursor: !prefs.bigCursor }))}
            {toggleBtn(prefs.stopAnimations, 'עצירת אנימציות', () => update({ stopAnimations: !prefs.stopAnimations }))}
          </div>

          <button
            type="button"
            onClick={reset}
            style={{ marginTop: 12, width: '100%', padding: '10px 0', borderRadius: 8, border: '1px solid #ef4444', background: '#fff', color: '#dc2626', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
          >
            איפוס הגדרות
          </button>

          <a
            href="/accessibility"
            style={{ display: 'block', textAlign: 'center', marginTop: 12, fontSize: 14, fontWeight: 700, color: '#1565C0', textDecoration: 'underline' }}
          >
            הצהרת הנגישות
          </a>
        </div>
      )}
    </>
  );
}
